import Pikaday from 'pikaday';

const selectors = {
  checkbox: '[data-recipient-fields-checkbox]',
  content: '[data-recipient-fields-content]',
  datepicker: '[data-datepicker]',
  datepickerInput: '[data-datepicker-input]',
  datepickerContainer: '[data-datepicker-container]',
  datepickerToday: '[data-datepicker-today]',
  datepickerClear: '[data-datepicker-clear]',
};

class RecipientFields extends HTMLElement {
  constructor() {
    super();
    this.fields = [
      {name: '__shopify_send_gift_card_to_recipient', required: true, default: 'true'},
      {name: 'Recipient name', required: true, default: ''},
      {name: 'Recipient email', required: true, default: ''},
      {name: 'Send on', required: false, default: ''},
      {name: 'Message', required: false, default: ''},
    ];

    // Elements
    this.checkboxEl = this.querySelector(selectors.checkbox);
    this.contentEl = this.querySelector(selectors.content);
    this.datepickerEl = this.querySelector(selectors.datepicker);
    this.datepickerInput = this.querySelector(selectors.datepickerInput);
    this.datepickerContainer = this.querySelector(selectors.datepickerContainer);
    this.datepickerTodayBtn = this.querySelector(selectors.datepickerToday);
    this.datepickerClearBtn = this.querySelector(selectors.datepickerClear);

    // Events
    this.checkboxEl.addEventListener('change', this.toggleForm.bind(this));
    this.datepickerInput.addEventListener('click', this.showDatepicker.bind(this));
    this.datepickerInput.addEventListener('focus', this.showDatepicker.bind(this));
    this.datepickerInput.addEventListener('blur', this.handleDatepickerBlur.bind(this));
    this.datepickerTodayBtn.addEventListener('click', this.datepickerToday.bind(this));
    this.datepickerClearBtn.addEventListener('click', this.datepickerClear.bind(this));

    // Custom datepicker
    this.datepicker = new Pikaday({
      bound: false,
      container: this.datepickerContainer,
      field: this.datepickerInput,
      firstDay: 1,
      format: 'YYYY-MM-DD',
      // Today
      minDate: new Date(),
      // 90 days in the future
      maxDate: (() => {
        const date = new Date();
        date.setDate(date.getDate() + 90);
        return date;
      })(),
      onSelect: () => {
        this.hideDatepicker();
      },
      toString: (date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
      },
      i18n: {
        previousMonth: 'Previous Month',
        nextMonth: 'Next Month',
        months: [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ],
        weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        weekdaysShort: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      },
    });
  }

  toggleForm() {
    this.updateFields(this.checkboxEl.checked);

    if (this.checkboxEl.checked) {
      this.contentEl.setAttribute('aria-hidden', 'false');
      this.checkboxEl.setAttribute('aria-expanded', 'true');
      return;
    }

    this.contentEl.setAttribute('aria-hidden', 'true');
    this.checkboxEl.setAttribute('aria-expanded', 'false');
  }

  updateFields(active) {
    this.fields.forEach((field) => {
      const fieldEl = this.querySelector(`[name="properties[${field.name}]"]`);
      if (!fieldEl) return;

      fieldEl.required = active ? field.required : false;
      fieldEl.value = field.default;
    });
  }

  showDatepicker() {
    this.datepicker.show();
    this.datepickerEl.setAttribute('aria-hidden', 'false');
  }

  hideDatepicker() {
    this.datepicker.hide();
    this.datepickerEl.setAttribute('aria-hidden', 'true');
  }

  handleDatepickerBlur() {
    // Delay hiding the datepicker to allow time for other actions (like clicking 'Today')
    setTimeout(() => {
      if (!this.datepickerEl.contains(document.activeElement)) {
        this.hideDatepicker();
      }
    }, 100);
  }

  datepickerToday() {
    const today = new Date();
    this.datepicker.setDate(today);
    // Keep focus on the input after setting the date
    this.datepickerInput.focus();
  }

  datepickerClear() {
    this.datepicker.clear();
    this.hideDatepicker();
  }
}

export default function initRecipientFields() {
  customElements.define('recipient-fields', RecipientFields);
}
